/** @module @liquidefy/code-editor/filesystem/filesystem-search @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  SearchQuery,
  SearchMatch,
  SearchResult,
  SearchResultId,
  FileIndex,
  IndexId,
  SymbolInfo,
  SymbolKind,
  OTOperation,
  EditorResult,
  Range,
  Position,
  GlobPattern,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asSearchResultId,
  asIndexId,
  now,
} from '../code-editor.js';

// ═══ §2 CONTENT SEARCH — search within content, files, and batches ═══

/**
 * Search within a single string of content. Returns all matches.
 */
export function searchInContent(content: string, query: SearchQuery): SearchMatch[] {
  const regex = buildSearchRegex(query);
  if (!regex) return [];

  const lines = content.split('\n');
  const matches: SearchMatch[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let match: RegExpExecArray | null;
    const lineRegex = new RegExp(regex.source, regex.flags);

    while ((match = lineRegex.exec(line!)) !== null) {
      const startCol = match.index;
      const endCol = startCol + match[0].length;

      matches.push({
        range: {
          start: { line: i, column: startCol },
          end: { line: i, column: endCol },
        },
        preview: line!,
        lineNumber: i,
      });

      if (!lineRegex.global) break;
      if (match[0].length === 0) {
        lineRegex.lastIndex++;
      }
    }
  }

  if (query.maxResults !== undefined && matches.length > query.maxResults) {
    return matches.slice(0, query.maxResults);
  }

  return matches;
}

/**
 * Search within a single file's content. Returns a SearchResult with the file uri.
 */
export function searchInFile(content: string, uri: string, query: SearchQuery): SearchResult {
  const matches = searchInContent(content, query);
  return {
    id: asSearchResultId(generateId('sr')),
    uri,
    matches,
  };
}

/**
 * Batch search across multiple files. Each file is { uri, content }.
 */
export function searchInFiles(
  files: ReadonlyArray<{ uri: string; content: string }>,
  query: SearchQuery
): SearchResult[] {
  const results: SearchResult[] = [];

  for (const file of files) {
    if (query.includePattern && !simpleGlobMatch(file.uri, query.includePattern)) {
      continue;
    }
    if (query.excludePattern && simpleGlobMatch(file.uri, query.excludePattern)) {
      continue;
    }

    const result = searchInFile(file.content, file.uri, query);
    if (result.matches.length > 0) {
      results.push(result);
    }
  }

  return results;
}

/**
 * Build a RegExp from a SearchQuery. Returns null if the pattern is invalid.
 */
export function buildSearchRegex(query: SearchQuery): RegExp | null {
  let pattern = query.pattern;
  if (!pattern) return null;

  if (!query.isRegex) {
    pattern = escapeRegExp(pattern);
  }

  if (query.isWholeWord) {
    pattern = `\\b${pattern}\\b`;
  }

  const flags = query.isCaseSensitive ? 'g' : 'gi';

  try {
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
}

/**
 * Extract context around a match with highlight markers.
 */
export function highlightMatch(
  content: string,
  match: SearchMatch,
  before: number = 0,
  after: number = 0
): string {
  const lines = content.split('\n');
  const startLine = Math.max(0, match.lineNumber - before);
  const endLine = Math.min(lines.length - 1, match.lineNumber + after);

  const contextLines: string[] = [];
  for (let i = startLine; i <= endLine; i++) {
    if (i === match.lineNumber) {
      const line = lines[i]!;
      const pre = line.slice(0, match.range.start.column);
      const highlighted = line.slice(match.range.start.column, match.range.end.column);
      const post = line.slice(match.range.end.column);
      contextLines.push(`${pre}«${highlighted}»${post}`);
    } else {
      contextLines.push(lines[i]!);
    }
  }

  return contextLines.join('\n');
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function simpleGlobMatch(path: string, pattern: string): boolean {
  const regexStr = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*\*/g, '§DOUBLESTAR§')
    .replace(/\*/g, '[^/]*')
    .replace(/§DOUBLESTAR§/g, '.*')
    .replace(/\?/g, '[^/]');

  try {
    return new RegExp(`^${regexStr}$`).test(path);
  } catch {
    return false;
  }
}


// ═══ §3 SEARCH FILTERING — filter, group, sort, limit results ═══

/**
 * Filter results to only include files matching the include pattern.
 */
export function filterByInclude(results: SearchResult[], pattern: GlobPattern): SearchResult[] {
  return results.filter(r => simpleGlobMatch(r.uri, pattern));
}

/**
 * Filter results to exclude files matching the exclude pattern.
 */
export function filterByExclude(results: SearchResult[], pattern: GlobPattern): SearchResult[] {
  return results.filter(r => !simpleGlobMatch(r.uri, pattern));
}

/**
 * Limit total number of results.
 */
export function limitResults(results: SearchResult[], max: number): SearchResult[] {
  let total = 0;
  const limited: SearchResult[] = [];

  for (const result of results) {
    if (total >= max) break;
    const remaining = max - total;
    if (result.matches.length <= remaining) {
      limited.push(result);
      total += result.matches.length;
    } else {
      limited.push({
        ...result,
        matches: result.matches.slice(0, remaining),
      });
      total = max;
    }
  }

  return limited;
}

/**
 * Group flat match results by file URI.
 */
export function groupResultsByFile(results: SearchResult[]): Map<string, SearchMatch[]> {
  const grouped = new Map<string, SearchMatch[]>();

  for (const result of results) {
    const existing = grouped.get(result.uri);
    if (existing) {
      existing.push(...result.matches);
    } else {
      grouped.set(result.uri, [...result.matches]);
    }
  }

  return grouped;
}

/**
 * Sort results by relevance: more matches in a file = more relevant.
 * Exact substring matches in filename boost relevance.
 */
export function sortResultsByRelevance(results: SearchResult[], query: SearchQuery): SearchResult[] {
  const pattern = query.pattern.toLowerCase();
  return [...results].sort((a, b) => {
    const aNameMatch = a.uri.toLowerCase().includes(pattern) ? 100 : 0;
    const bNameMatch = b.uri.toLowerCase().includes(pattern) ? 100 : 0;
    const scoreA = a.matches.length + aNameMatch;
    const scoreB = b.matches.length + bNameMatch;
    return scoreB - scoreA;
  });
}

/**
 * Sort results alphabetically by file path.
 */
export function sortResultsByPath(results: SearchResult[]): SearchResult[] {
  return [...results].sort((a, b) => a.uri < b.uri ? -1 : a.uri > b.uri ? 1 : 0);
}

/**
 * Sort results by match count, most matches first.
 */
export function sortResultsByMatchCount(results: SearchResult[]): SearchResult[] {
  return [...results].sort((a, b) => b.matches.length - a.matches.length);
}

/**
 * Count total matches across all results.
 */
export function totalMatchCount(results: SearchResult[]): number {
  let count = 0;
  for (const r of results) {
    count += r.matches.length;
  }
  return count;
}


// ═══ §4 REPLACE — replace in content, preview, preserve case ═══

/**
 * Replace matched ranges in content with the replacement string.
 */
export function replaceInContent(
  content: string,
  matches: SearchMatch[],
  replacement: string,
  preserveCase: boolean
): string {
  if (matches.length === 0) return content;

  const sorted = [...matches].sort((a, b) => {
    if (a.lineNumber !== b.lineNumber) return b.lineNumber - a.lineNumber;
    return b.range.start.column - a.range.start.column;
  });

  const lines = content.split('\n');

  for (const match of sorted) {
    const line = lines[match.lineNumber];
    if (!line && line !== '') continue;

    const original = line.slice(match.range.start.column, match.range.end.column);
    const rep = preserveCase ? preserveCaseReplace(original, replacement) : replacement;

    lines[match.lineNumber] =
      line.slice(0, match.range.start.column) + rep + line.slice(match.range.end.column);
  }

  return lines.join('\n');
}

/**
 * Replace matches in a file's content. Returns new content and replacement count.
 */
export function replaceInFile(
  content: string,
  uri: string,
  query: SearchQuery,
  replacement: string,
  preserveCase: boolean
): { content: string; count: number } {
  const result = searchInFile(content, uri, query);
  const newContent = replaceInContent(content, result.matches, replacement, preserveCase);
  return { content: newContent, count: result.matches.length };
}

/**
 * Preview what replacements would look like without applying them.
 * Returns lines showing before → after for each match.
 */
export function previewReplace(
  content: string,
  matches: SearchMatch[],
  replacement: string,
  preserveCase: boolean
): string[] {
  const lines = content.split('\n');
  const previews: string[] = [];

  for (const match of matches) {
    const line = lines[match.lineNumber];
    if (!line && line !== '') continue;

    const original = line.slice(match.range.start.column, match.range.end.column);
    const rep = preserveCase ? preserveCaseReplace(original, replacement) : replacement;

    const before = line;
    const after =
      line.slice(0, match.range.start.column) + rep + line.slice(match.range.end.column);

    previews.push(`L${match.lineNumber + 1}: ${before}`);
    previews.push(`  → ${after}`);
  }

  return previews;
}

/**
 * Replace text while preserving the casing pattern of the original.
 * - ALL_CAPS → ALL_CAPS replacement
 * - PascalCase → PascalCase replacement
 * - camelCase → camelCase replacement
 * - lowercase → lowercase replacement
 * - Otherwise → replacement as-is
 */
export function preserveCaseReplace(original: string, replacement: string): string {
  if (original.length === 0) return replacement;

  if (original === original.toUpperCase() && /[A-Z]/.test(original)) {
    return replacement.toUpperCase();
  }

  if (original === original.toLowerCase()) {
    return replacement.toLowerCase();
  }

  if (original[0] === original[0]!.toUpperCase() && original.slice(1) === original.slice(1).toLowerCase()) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase();
  }

  if (original[0] === original[0]!.toLowerCase() && /[A-Z]/.test(original)) {
    return replacement.charAt(0).toLowerCase() + replacement.slice(1);
  }

  return replacement;
}


// ═══ §5 INDEXING — file index creation, status management ═══

/**
 * Create a new FileIndex with the given id and type.
 */
export function createFileIndex(id: string, type: FileIndex['type']): FileIndex {
  return {
    id: asIndexId(id),
    type,
    status: 'building',
    fileCount: 0,
    lastUpdated: now(),
  };
}

/**
 * Update the status of a file index.
 */
export function updateIndexStatus(index: FileIndex, status: FileIndex['status']): FileIndex {
  return { ...index, status, lastUpdated: now() };
}

/**
 * Check if an index is ready for queries.
 */
export function isIndexReady(index: FileIndex): boolean {
  return index.status === 'ready';
}

/**
 * Check if an index is stale based on a maximum age in milliseconds.
 */
export function isIndexStale(index: FileIndex, maxAge: number): boolean {
  return (now() - index.lastUpdated) > maxAge;
}

/**
 * Determine whether to rebuild the index based on number of changes.
 */
export function shouldReindex(index: FileIndex, changes: number): boolean {
  return changes >= INDEX_REBUILD_THRESHOLD || index.status === 'error';
}

/**
 * Number of file changes that trigger a full reindex instead of incremental update.
 */
export const INDEX_REBUILD_THRESHOLD = 50;


// ═══ §6 SYMBOL INDEX — build, search, filter, update symbol indices ═══

interface SymbolIndex {
  readonly symbols: SymbolInfo[];
  readonly byKind: Map<SymbolKind, SymbolInfo[]>;
  readonly byFile: Map<string, SymbolInfo[]>;
  readonly byName: Map<string, SymbolInfo[]>;
}

/**
 * Build a searchable symbol index from a flat list of symbols.
 */
export function buildSymbolIndex(symbols: SymbolInfo[]): SymbolIndex {
  const byKind = new Map<SymbolKind, SymbolInfo[]>();
  const byFile = new Map<string, SymbolInfo[]>();
  const byName = new Map<string, SymbolInfo[]>();

  for (const sym of symbols) {
    const kindList = byKind.get(sym.kind);
    if (kindList) kindList.push(sym);
    else byKind.set(sym.kind, [sym]);

    const fileList = byFile.get(sym.uri);
    if (fileList) fileList.push(sym);
    else byFile.set(sym.uri, [sym]);

    const nameLower = sym.name.toLowerCase();
    const nameList = byName.get(nameLower);
    if (nameList) nameList.push(sym);
    else byName.set(nameLower, [sym]);
  }

  return { symbols, byKind, byFile, byName };
}

/**
 * Search the symbol index by query string. Supports fuzzy prefix matching.
 */
export function searchSymbols(index: SymbolIndex, query: string, maxResults: number = 50): SymbolInfo[] {
  if (!query) return index.symbols.slice(0, maxResults);

  const lower = query.toLowerCase();
  const results: Array<{ symbol: SymbolInfo; score: number }> = [];

  for (const sym of index.symbols) {
    const nameLower = sym.name.toLowerCase();

    if (nameLower === lower) {
      results.push({ symbol: sym, score: 100 });
    } else if (nameLower.startsWith(lower)) {
      results.push({ symbol: sym, score: 80 });
    } else if (nameLower.includes(lower)) {
      results.push({ symbol: sym, score: 60 });
    } else if (fuzzyMatch(lower, nameLower)) {
      results.push({ symbol: sym, score: 40 });
    }
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, maxResults).map(r => r.symbol);
}

/**
 * Get all symbols of a specific kind.
 */
export function symbolsByKind(index: SymbolIndex, kind: SymbolKind): SymbolInfo[] {
  return index.byKind.get(kind) ?? [];
}

/**
 * Get all symbols in a specific file.
 */
export function symbolsByFile(index: SymbolIndex, uri: string): SymbolInfo[] {
  return index.byFile.get(uri) ?? [];
}

/**
 * Get symbol completions for a prefix string.
 */
export function symbolCompletions(index: SymbolIndex, prefix: string): SymbolInfo[] {
  if (!prefix) return [];
  const lower = prefix.toLowerCase();
  return index.symbols.filter(s => s.name.toLowerCase().startsWith(lower));
}

/**
 * Update symbols for a single file in the index.
 */
export function updateSymbolIndex(index: SymbolIndex, uri: string, symbols: SymbolInfo[]): SymbolIndex {
  const filtered = index.symbols.filter(s => s.uri !== uri);
  const updated = [...filtered, ...symbols];
  return buildSymbolIndex(updated);
}

/**
 * Remove all symbols for a file from the index.
 */
export function removeFileFromIndex(index: SymbolIndex, uri: string): SymbolIndex {
  const filtered = index.symbols.filter(s => s.uri !== uri);
  return buildSymbolIndex(filtered);
}

function fuzzyMatch(query: string, target: string): boolean {
  let qi = 0;
  let ti = 0;
  while (qi < query.length && ti < target.length) {
    if (query[qi] === target[ti]) {
      qi++;
    }
    ti++;
  }
  return qi === query.length;
}


// ═══ §7 COLLABORATION — OT operations for concurrent editing ═══

/**
 * Transform op1 against op2 so that op1 can be applied after op2.
 * Returns the transformed version of op1.
 */
export function transformOTOperation(op1: OTOperation, op2: OTOperation): OTOperation {
  if (op1.type === 'retain') return op1;
  if (op2.type === 'retain') return op1;

  if (op1.type === 'insert' && op2.type === 'insert') {
    if (op1.position < op2.position) return op1;
    if (op1.position > op2.position) {
      return { ...op1, position: op1.position + (op2.text?.length ?? 0) };
    }
    if (op1.clientId < op2.clientId) return op1;
    return { ...op1, position: op1.position + (op2.text?.length ?? 0) };
  }

  if (op1.type === 'insert' && op2.type === 'delete') {
    if (op1.position <= op2.position) return op1;
    const deleteEnd = op2.position + (op2.count ?? 0);
    if (op1.position >= deleteEnd) {
      return { ...op1, position: op1.position - (op2.count ?? 0) };
    }
    return { ...op1, position: op2.position };
  }

  if (op1.type === 'delete' && op2.type === 'insert') {
    if (op2.position <= op1.position) {
      return { ...op1, position: op1.position + (op2.text?.length ?? 0) };
    }
    const deleteEnd = op1.position + (op1.count ?? 0);
    if (op2.position >= deleteEnd) return op1;
    const beforeInsert = op2.position - op1.position;
    const afterInsert = (op1.count ?? 0) - beforeInsert;
    return {
      ...op1,
      count: beforeInsert + (op2.text?.length ?? 0) + afterInsert,
    };
  }

  if (op1.type === 'delete' && op2.type === 'delete') {
    const end1 = op1.position + (op1.count ?? 0);
    const end2 = op2.position + (op2.count ?? 0);

    if (end1 <= op2.position) return op1;

    if (op1.position >= end2) {
      return { ...op1, position: op1.position - (op2.count ?? 0) };
    }

    if (op1.position >= op2.position && end1 <= end2) {
      return { ...op1, position: op2.position, count: 0 };
    }

    if (op1.position < op2.position && end1 > end2) {
      return { ...op1, count: (op1.count ?? 0) - (op2.count ?? 0) };
    }

    if (op1.position < op2.position) {
      return { ...op1, count: op2.position - op1.position };
    }

    return {
      ...op1,
      position: op2.position,
      count: end1 - end2,
    };
  }

  return op1;
}

/**
 * Apply an OT operation to content. Returns the resulting string.
 */
export function applyOTOperation(content: string, op: OTOperation): string {
  switch (op.type) {
    case 'insert':
      return content.slice(0, op.position) + (op.text ?? '') + content.slice(op.position);

    case 'delete': {
      const count = op.count ?? 0;
      return content.slice(0, op.position) + content.slice(op.position + count);
    }

    case 'retain':
      return content;
  }
}

/**
 * Compose two sequential OT operations into a single equivalent operation.
 * Returns the composed operation, or the second operation if composition
 * is not possible (different types at different positions).
 */
export function composeOTOperations(op1: OTOperation, op2: OTOperation): OTOperation {
  if (op1.type === 'retain') return op2;
  if (op2.type === 'retain') return op1;

  if (op1.type === 'insert' && op2.type === 'insert') {
    if (op2.position === op1.position + (op1.text?.length ?? 0)) {
      return {
        ...op1,
        text: (op1.text ?? '') + (op2.text ?? ''),
        revision: op2.revision,
      };
    }
  }

  if (op1.type === 'delete' && op2.type === 'delete') {
    if (op2.position === op1.position) {
      return {
        ...op1,
        count: (op1.count ?? 0) + (op2.count ?? 0),
        revision: op2.revision,
      };
    }
  }

  return op2;
}

/**
 * Create an insert OT operation.
 */
export function createInsertOp(position: number, text: string, clientId: string, revision: number): OTOperation {
  return { type: 'insert', position, text, clientId, revision };
}

/**
 * Create a delete OT operation.
 */
export function createDeleteOp(position: number, count: number, clientId: string, revision: number): OTOperation {
  return { type: 'delete', position, count, clientId, revision };
}

/**
 * Create a retain OT operation.
 */
export function createRetainOp(count: number, clientId: string, revision: number): OTOperation {
  return { type: 'retain', position: 0, count, clientId, revision };
}

/**
 * Check if an OT operation is a no-op (retain with no effect, empty insert, zero delete).
 */
export function isOTNoOp(op: OTOperation): boolean {
  if (op.type === 'retain') return true;
  if (op.type === 'insert') return !op.text || op.text.length === 0;
  if (op.type === 'delete') return !op.count || op.count === 0;
  return false;
}

/**
 * Invert an OT operation (for undo). Needs the original content to compute inverses.
 */
export function invertOTOperation(op: OTOperation, content: string): OTOperation {
  switch (op.type) {
    case 'insert':
      return {
        type: 'delete',
        position: op.position,
        count: op.text?.length ?? 0,
        clientId: op.clientId,
        revision: op.revision + 1,
      };

    case 'delete': {
      const deletedText = content.slice(op.position, op.position + (op.count ?? 0));
      return {
        type: 'insert',
        position: op.position,
        text: deletedText,
        clientId: op.clientId,
        revision: op.revision + 1,
      };
    }

    case 'retain':
      return op;
  }
}
